import React, { useEffect, useState } from "react";
import jsFileDownload from "js-file-download";
import { pdf } from "@react-pdf/renderer";
import { toast } from "../../../../toast";
import { Table } from "flowbite-react";
import AdminLayout from "../../Layout";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetAssignmentQuery,
  useGetAllAssignmentQuestionQuery,
  useGetAssignmentLeaderBoardQuery,
  useSendEmailMutation,
  useGetClustersByCampusQuery,
} from "../../../../graphql/generated.tsx";
import PDFDocument from "../../Components/AssignmentPdfDocument.jsx";
import { FaMedal } from "react-icons/fa";
import { SkeletonRow } from "../../../Student/Components/LeaderBoardSkeleton.jsx";
import { IoMdArrowRoundBack } from "react-icons/io";
import LightSpinner from "../../../../components/LightSpinner.jsx";
import Pagination from "../../../../components/Pagination/index.jsx";
import { FaDownload } from "react-icons/fa6";
import { MdTableChart, MdPictureAsPdf } from "react-icons/md";
import {
  AiOutlineBarChart,
  AiOutlineCalendar,
  AiOutlineCheckCircle,
  AiOutlineClockCircle,
  AiOutlineFileText,
  AiOutlineTeam,
} from "react-icons/ai";
import { HiUsers } from "react-icons/hi2";

const AssignmentReport = () => {
  const { assignment_id, campusName } = useParams();
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emails, setEmails] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isLoad, setIsLoad] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => {
      setIsLoad(false);
    }, 2000);
  }, []);

  const { data: LeaderBoardData, loading, error } =
    useGetAssignmentLeaderBoardQuery({
      variables: { assignmentId: assignment_id, campus_name: campusName },
    });

  const { data: Assignment } = useGetAssignmentQuery({
    variables: { id: assignment_id },
  });

  const { data: assignmentQuestionsData } = useGetAllAssignmentQuestionQuery({
    variables: { assignmentId: assignment_id, page: 1, limit: 1000 },
    skip: !assignment_id,
  });

  // Fetch all clusters to determine which ones have this assignment
  const { data: clustersData } = useGetClustersByCampusQuery({
    variables: { campusName: campusName, limit: 200 },
    skip: !campusName,
  });

  const [sendEmail] = useSendEmailMutation();

  // ✅ CLEAN CHEAT REASONS - Returns array for line-by-line display
  const extractCheatReasonsOnly = (cheatReasons) => {
    if (!cheatReasons) return null;
    return cheatReasons
      .split("\n")
      .map((line) => line.split("|")[0])
      .map((reason) => reason.trim())
      .filter((reason) => reason && reason !== "null");
  };

  // ✅ Format for CSV export (comma-separated)
  const formatCheatReasonsForCSV = (cheatReasons) => {
    const reasons = extractCheatReasonsOnly(cheatReasons);
    if (!reasons || reasons.length === 0) return "-";
    return reasons.join(", ");
  };

  // ✅ Render cheat reasons for table display
  const renderCheatReasons = (cheatReasons) => {
    const reasons = extractCheatReasonsOnly(cheatReasons);
    if (!reasons || reasons.length === 0) return "-";
    return (
      <div className="space-y-1">
        {reasons.map((reason, index) => (
          <div key={index} className="text-sm">
            {reason}
          </div>
        ))}
      </div>
    );
  };

  const parseEmailList = (raw) =>
    String(raw || "")
      .split(/[\s,;]+/)
      .map((email) => email.trim())
      .filter(Boolean);

  const validateEmails = (emailString) => {
    const emailArray = parseEmailList(emailString);
    if (emailArray.length === 0) return false;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailArray.every((email) => emailPattern.test(email));
  };

  const handleSendEmail = async () => {
    if (!validateEmails(emails)) {
      setEmailError(
        "Enter one or more valid emails, separated by commas, spaces, or newlines."
      );
      return;
    }

    const emailList = parseEmailList(emails);

    const csvHeaders = [
      "Name",
      "Roll No",
      "Department",
      "Batch Enrolled",
      "Questions Attended",
      "Questions Completed",
      "Progress (%)",
      "Cheat Reasons",
      "Score",
    ];

    const dataToUse = LeaderBoardData?.getAssignmentLeaderBoard ?? [];

    const csvContent =
      csvHeaders.join(",") +
      "\n" +
      dataToUse
        .map((assign) => {
          const questionAttended = assign.questions.filter(
            (question) => question.score > 0
          ).length;

          const questionCompleted = assign.questions.filter(
            (question) => question.score === question.AllotedTotalMarks
          ).length;

          const progressPercentage = Math.round(
            (questionCompleted / assign.totalNumberOfQuestion) * 100
          );

          const cleanedCheatReasons = formatCheatReasonsForCSV(
            assign.cheat_reasons
          );

          return [
            `"${assign.student.name}"`,
            assign.student.rollNo,
            `"${assign.student.department || "-"}"`,
            `${assign.clusterNames}`,
            questionAttended,
            questionCompleted,
            `${progressPercentage}%`,
            `"${cleanedCheatReasons}"`,
            assign.totalScore,
          ].join(",");
        })
        .join("\n");

    try {
      await sendEmail({
        variables: {
          emailInput: {
            emailList,
            csvContent,
            assignmentName: Assignment?.getAssignment.name,
          },
        },
      });

      toast.success("Email sent successfully!");
      setShowEmailDropdown(false);
      setEmails("");
    } catch (error) {
      toast.error("Failed to send email.");
    }
  };

  const downloadCSV = () => {
    const dataToUse = LeaderBoardData?.getAssignmentLeaderBoard ?? [];

    const csvHeaders = [
      "Position",
      "Name",
      "Roll No",
      "Department",
      "Batch Enrolled",
      "Questions Attended",
      "Questions Completed",
      "Progress (%)",
      "Cheat Reasons",
      "Score",
    ];

    const csvContent =
      csvHeaders.join(",") +
      "\n" +
      dataToUse
        .map((assign) => {
          const questionAttended = assign.questions.filter(
            (q) => q.score > 0
          ).length;

          const questionCompleted = assign.questions.filter(
            (q) => q.score === q.AllotedTotalMarks
          ).length;

          const progressPercentage = Math.round(
            (questionCompleted / assign.totalNumberOfQuestion) * 100
          );

          const cleanedCheatReasons = formatCheatReasonsForCSV(
            assign.cheat_reasons
          );

          return [
            `${assign.position || ""}`,
            `"${assign.student.name}"`,
            `"${assign.student.rollNo}"`,
            `"${assign.student.department || "-"}"`,
            `"${assign.clusterNames}"`,
            `"${questionAttended}"`,
            `"${questionCompleted}"`,
            `"${progressPercentage}%"`,
            `"${cleanedCheatReasons}"`,
            `"${assign.totalScore}"`,
          ].join(",");
        })
        .join("\n");

    jsFileDownload(
      csvContent,
      `${Assignment?.getAssignment?.name || "leaderboard"}.csv`
    );
  };

  const handleDownloadPDF = async () => {
    if (pdfLoading) return;

    setPdfLoading(true);
    try {
      const blob = await pdf(
        <PDFDocument
          students={tableData}
          assignmentName={Assignment?.getAssignment.name}
        />
      ).toBlob();

      const safeFileName = (Assignment?.getAssignment?.name || "leaderboard")
        .replace(/[\\/:*?"<>|]+/g, "-")
        .trim();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${safeFileName}-leaderboard.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setShowDownloadDropdown(false);
    } catch (error) {
      toast.error("Failed to generate PDF. Please try again.");
    } finally {
      setPdfLoading(false);
    }
  };

  const getPositionIcon = (position) => {
    if (position === 1)
      return <FaMedal className="mx-auto text-center text-yellow-500" />;
    if (position === 2)
      return <FaMedal className="mx-auto text-center text-gray-400" />;
    if (position === 3)
      return <FaMedal className="mx-auto text-center text-yellow-800" />;
    return position;
  };

  const renderProgressSegments = (assign) => {
    const completedQuestions = assign.questions.filter(
      (q) => q.score === q.AllotedTotalMarks
    ).length;

    const completionRate =
      completedQuestions / assign.totalNumberOfQuestion;

    const segmentCount = 3;
    const filledSegments = Math.round(completionRate * segmentCount);

    return (
      <div className="flex space-x-1">
        {[...Array(segmentCount)].map((_, index) => (
          <div
            key={index}
            className={`w-6 h-1.5 rounded-full ${
              index < filledSegments ? "bg-primary" : "bg-gray-200"
            }`}
          />
        ))}
      </div>
    );
  };

  const tableData =
    LeaderBoardData?.getAssignmentLeaderBoard ?? [];

  // Get clusters where assignment is actually assigned
  const [assignmentClusterIds, setAssignmentClusterIds] = useState([]);
  const [assignmentClusterNames, setAssignmentClusterNames] = useState([]);

  useEffect(() => {
    if (!clustersData?.getClustersByCampus || !assignment_id || !campusName)
      return;

    // Check each cluster to see if it has this assignment
    const checkClusters = async () => {
      const clusterIds = [];
      const clusterNames = [];

      for (const cluster of clustersData.getClustersByCampus) {
        try {
          // This is a simplified check - ideally backend should provide this
          // For now, we'll use the clusters from submitted students as a proxy
          const studentClusters = [
            ...new Set(
              tableData
                .map((student) => student.clusterNames)
                .filter(Boolean)
                .flatMap((names) => names.split(",").map((n) => n.trim()))
            ),
          ];

          if (studentClusters.includes(cluster.name)) {
            clusterIds.push(cluster.id);
            clusterNames.push(cluster.name);
          }
        } catch (error) {
          console.error("Error checking cluster:", error);
        }
      }

      setAssignmentClusterIds(clusterIds);
      setAssignmentClusterNames(clusterNames);
    };

    checkClusters();
  }, [clustersData, assignment_id, campusName, tableData]);

  // Pagination calculations
  const totalPages = Math.ceil(tableData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = tableData.slice(startIndex, endIndex);

  // ✅ Statistics
  const totalStudents = tableData.length;
  const submittedStudents = tableData.filter(
    (student) => (student.totalScore ?? 0) > 0
  ).length;
  const completionRate =
    totalStudents > 0
      ? Math.round((submittedStudents / totalStudents) * 100)
      : 0;
  const averageScore =
    totalStudents > 0
      ? Math.round(
          tableData.reduce((sum, student) => sum + (student.totalScore ?? 0), 0) /
            totalStudents
        )
      : 0;

  const durationValue = Assignment?.getAssignment?.duration;
  const durationLabel = Number.isFinite(durationValue)
    ? (() => {
        const hours = Math.floor(durationValue);
        const minutes = Math.round((durationValue - hours) * 60);

        if (hours === 0) return `${minutes} min`;
        if (minutes === 0) return `${hours} hr`;

        return `${hours} hr ${minutes} min`;
      })()
    : "N/A";
  const totalQuestions =
    Assignment?.getAssignment?.totalQuestions ??
    assignmentQuestionsData?.getAllAssignmentQuestion?.length ??
    "N/A";
  const totalMarks = assignmentQuestionsData?.getAllAssignmentQuestion?.length
    ? assignmentQuestionsData.getAllAssignmentQuestion.reduce((sum, question) => {
        const marks = Number(question.marks);
        return sum + (Number.isFinite(marks) ? marks : 0);
      }, 0)
    : "N/A";

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const reportDate = formatDate(
    Assignment?.getAssignment?.due_date || Assignment?.getAssignment?.createdAt
  );

  return (
    <AdminLayout>
      {isLoad || loading ? (
        <div className="flex items-center justify-center min-h-screen">
          <LightSpinner message={"loading data"} />
        </div>
      ) : (
        <div className="min-h-screen px-4 pt-5 pb-8 bg-white sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.history.back()}
                className="p-2 transition-colors rounded-lg hover:bg-gray-100"
              >
                <IoMdArrowRoundBack className="w-5 h-5 text-gray-600" />
              </button>

              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
                {Assignment?.getAssignment.name || "Test Assignment"}
              </h1>
            </div>

            <div className="flex flex-wrap gap-2 sm:justify-end">
              <button
                onClick={() =>
                  navigate("notAttendee", {
                    state: {
                      assignmentClusterIds: assignmentClusterIds,
                      assignmentClusterNames: assignmentClusterNames,
                      assignmentName: Assignment?.getAssignment.name,
                      campusName: campusName,
                    },
                  })
                }
                className="px-4 py-2.5 text-sm font-medium text-white rounded-lg bg-primary hover:bg-primary/90 transition-colors shadow-sm"
              >
                View Not Attended
              </button>

              <div className="relative">
                <button
                  onClick={() => setShowDownloadDropdown((prev) => !prev)}
                  className="inline-flex items-center justify-center w-10 h-10 text-gray-600 transition-all bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 hover:text-gray-900"
                  title="Download"
                  aria-label="Download"
                >
                  <FaDownload size={18} />
                </button>

                {showDownloadDropdown && (
                  <div className="absolute right-0 z-20 p-2 mt-2 bg-white border border-gray-200 shadow-lg top-full w-44 rounded-xl">
                    <p className="mb-2 px-2 text-[11px] font-semibold tracking-wide text-gray-500 uppercase">
                      Choose format
                    </p>

                    <button
                      onClick={() => {
                        downloadCSV();
                        setShowDownloadDropdown(false);
                      }}
                      className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <span className="rounded-lg bg-gray-100 p-1.5 text-gray-600">
                        <MdTableChart size={17} />
                      </span>
                      <span className="text-sm font-medium">CSV</span>
                    </button>

                    <button
                      onClick={handleDownloadPDF}
                      disabled={pdfLoading}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors ${
                        pdfLoading
                          ? "text-gray-400"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span
                        className={`rounded-lg p-1.5 ${
                          pdfLoading
                            ? "bg-gray-100 text-gray-400"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        <MdPictureAsPdf size={17} />
                      </span>
                      <span className="text-sm font-medium">PDF</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ✅ MODERN STATS CARDS */}
          <div className="mb-6 overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
            <div className="grid grid-cols-1 divide-y divide-gray-200 sm:grid-cols-2 sm:divide-y-0 sm:divide-x lg:grid-cols-6">
              {/* Duration */}
              <div className="flex items-center gap-3 px-6 py-4 text-left">
                <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                  <AiOutlineClockCircle className="w-5 h-5 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                    Duration
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {durationLabel}
                  </p>
                </div>
              </div>

              {/* Questions */}
              <div className="flex items-center gap-3 px-6 py-4 text-left">
                <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                  <AiOutlineFileText className="w-5 h-5 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                    Questions
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {totalQuestions}
                  </p>
                </div>
              </div>

              {/* Date */}
              <div className="flex items-center gap-3 px-6 py-4 text-left">
                <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                  <AiOutlineCalendar className="w-5 h-5 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                    Due Date
                  </p>
                  <p className="text-base font-bold text-gray-900 whitespace-nowrap">{reportDate}</p>
                </div>
              </div>

              {/* Students */}
              <div className="flex items-center gap-3 px-6 py-4 text-left">
                <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                  <HiUsers className="w-5 h-5 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                    Students
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {totalStudents}
                  </p>
                </div>
              </div>

              {/* Total Marks */}
              <div className="flex items-center gap-3 px-6 py-4 text-left">
                <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                  <AiOutlineBarChart className="w-5 h-5 text-slate-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                    Total Marks
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {totalMarks}
                  </p>
                </div>
              </div>

              {/* Avg Score */}
              <div className="flex items-center gap-3 px-6 py-4 text-left">
                <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 rounded-lg bg-slate-50 border border-slate-200">
                  <span className="text-base font-bold text-slate-600">★</span>
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                    Avg Score
                  </p>
                  <p className="text-base font-bold text-gray-900">
                    {averageScore}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div className="overflow-hidden bg-white border border-gray-200 shadow-sm rounded-xl">
            <div className="overflow-x-auto">
              <Table className="min-w-[720px]">
                <Table.Head style={{ backgroundColor: "#5c7a94" }}>
                  {[
                    "Position",
                    "Name",
                    "Roll No",
                    "Department",
                    "Batch Enrolled",
                    "Questions Attended",
                    "Questions Completed",
                    "Progress",
                    "Cheat Reasons",
                    "Score",
                  ].map((h) => (
                    <Table.HeadCell
                      key={h}
                      style={{ backgroundColor: "#5c7a94" }}
                      className="font-semibold text-center text-white"
                    >
                      {h}
                    </Table.HeadCell>
                  ))}
                </Table.Head>

                <Table.Body className="divide-y divide-gray-200">
                  {loading ? (
                    <>
                      <SkeletonRow col={10} />
                    </>
                  ) : (
                    paginatedData.map((assign, index) => (
                      <Table.Row
                        key={index}
                        className="transition-colors hover:bg-gray-50"
                      >
                        <Table.Cell className="text-center">
                          {getPositionIcon(assign.position)}
                        </Table.Cell>
                        <Table.Cell className="font-medium text-center text-gray-900">
                          {assign.student.name}
                        </Table.Cell>
                        <Table.Cell className="text-center text-gray-600">
                          {assign.student.rollNo}
                        </Table.Cell>
                        <Table.Cell className="text-center text-gray-600">
                          {assign.student.department || "-"}
                        </Table.Cell>
                        <Table.Cell className="text-center text-gray-600">
                          {assign.clusterNames}
                        </Table.Cell>
                        <Table.Cell className="text-center text-gray-600">
                          {assign.questions.filter((q) => q.score > 0).length}
                        </Table.Cell>
                        <Table.Cell className="text-center text-gray-600">
                          {
                            assign.questions.filter(
                              (q) => q.score === q.AllotedTotalMarks
                            ).length
                          }
                        </Table.Cell>
                        <Table.Cell className="text-center">
                          {renderProgressSegments(assign)}
                        </Table.Cell>
                        <Table.Cell className="text-center text-gray-600">
                          {renderCheatReasons(assign.cheat_reasons)}
                        </Table.Cell>
                        <Table.Cell className="font-semibold text-center text-gray-900">
                          {assign.totalScore}
                        </Table.Cell>
                      </Table.Row>
                    ))
                  )}
                </Table.Body>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 bg-white">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </div>
            )}
          </div>

          {error && (
            <div className="p-4 mt-4 text-sm text-red-600 border border-red-200 rounded-lg bg-red-50">
              Error loading leaderboard.
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AssignmentReport;